<template>
  <div
    ref="host"
    class="hero-field"
    :class="{ fallback }"
    :tabindex="interactive ? 0 : undefined"
  >
    <span
      v-if="fallback"
      v-for="p in fallbackPoints"
      :key="p.id"
      :style="p.style"
    />
  </div>
</template>
<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import * as THREE from "three";
import {
  DYNAMIC_NODE_LIMIT_DESKTOP,
  DYNAMIC_NODE_LIMIT_MOBILE,
  scheduleNextSpawn,
  shouldAnchorDynamicNode,
  shouldPauseDynamicMotion,
} from "@/utils/heroFieldMotion.mjs";
import { CREATION_SCENE_DURATION, NODE_BURST_DURATION } from "@/utils/opinionSceneFlow.mjs";

const props = withDefaults(
  defineProps<{ interactive?: boolean; paused?: boolean; progress?: number; selection?: boolean; burst?: number; bursting?: boolean }>(),
  { interactive: true, paused: false, progress: 0, selection: false, burst: 0, bursting: false }
);
const emit = defineEmits<{
  (e: "connect", count: number): void;
  (e: "create"): void;
}>();

const host = ref<HTMLElement>();
const fallback = ref(false);

let renderer: THREE.WebGLRenderer | undefined,
  scene: THREE.Scene | undefined,
  camera: THREE.PerspectiveCamera | undefined,
  group: THREE.Group | undefined,
  nodeMesh: THREE.InstancedMesh | undefined,
  dynamicMesh: THREE.InstancedMesh | undefined,
  linkMesh: THREE.LineSegments | undefined,
  dynamicLinkMesh: THREE.LineSegments | undefined,
  linkMat: THREE.ShaderMaterial | undefined,
  grid: THREE.GridHelper | undefined,
  dust: THREE.Points | undefined,
  dustTrails: THREE.LineSegments | undefined,
  dustTrailMat: THREE.LineBasicMaterial | undefined,
  dustPrev: THREE.Vector3[] = [],
  dustTrailPositions: Float32Array = new Float32Array(0),
  observer: ResizeObserver | undefined,
  frame = 0,
  visible = true,
  introT = 0;
let selectionMix = 0;
let selectionTarget = false;
let selectionFrom = 0;
let selectionStartedAt = 0;
const selectionLookAt = new THREE.Vector3();
const selectionDuration = CREATION_SCENE_DURATION;
let burstSeen = 0;
let burstStartedAt = -1;
let burstIndex = -1;
let burstMesh: THREE.Mesh | undefined;
const burstStart = new THREE.Vector3();
const burstEnd = new THREE.Vector3();
const burstForward = new THREE.Vector3();
let transparentClear = false;

let base: THREE.Vector3[] = [],
  current: THREE.Vector3[] = [],
  galaxyBase: THREE.Vector3[] = [],
  nodeDelay: number[] = [],
  nodeWeight: number[] = [],
  nodeImpact: number[] = [],
  nodeScreen: { x: number; y: number }[] = [];
let activeLinks: { s: number; t: number; type: number }[] = [];
let treeLinks: { s: number; t: number; type: number }[] = [];
let linkPosition: Float32Array = new Float32Array(0),
  linkType: Float32Array = new Float32Array(0),
  linkEmphasis: Float32Array = new Float32Array(0);
let linkFade = 0;
let dustBase: THREE.Vector3[] = [],
  dustPositions: Float32Array = new Float32Array(0),
  dustCur: THREE.Vector3[] = [],
  dustVel: THREE.Vector3[] = [];
let meteors: THREE.LineSegments | undefined,
  meteorCur: THREE.Vector3[] = [],
  meteorVel: THREE.Vector3[] = [],
  meteorOmega: Float32Array = new Float32Array(0),
  meteorPositions: Float32Array = new Float32Array(0);
let nodeWorldV: THREE.Vector3[] = [];
type DynamicNode = {
  slot: number;
  state: "arriving" | "transient" | "anchored" | "departing";
  position: THREE.Vector3;
  start: THREE.Vector3;
  target: THREE.Vector3;
  departureTarget: THREE.Vector3;
  linkedCore: number;
  age: number;
  ttl: number;
  departureAt: number;
  willAnchor: boolean;
  alpha: number;
};
let dynamicNodes: Array<DynamicNode | undefined> = [];
let dynamicSlots = DYNAMIC_NODE_LIMIT_DESKTOP;
let nextDynamicSpawnAt = 0;
const dynamicQuaternion = new THREE.Quaternion().setFromEuler(
  new THREE.Euler(Math.PI / 2, 0, 0)
);
let sparks: THREE.Points | undefined,
  sparkPositions: Float32Array = new Float32Array(0),
  sparkColors: Float32Array = new Float32Array(0),
  sparkLife: Float32Array = new Float32Array(0),
  sparkVel: Float32Array = new Float32Array(0),
  sparkBase: Float32Array = new Float32Array(0),
  sparkCursor = 0;
let impactColors: number[] = [];

let focusIndex = -1;
let lastFocus = -1;
const adjacent = new Set<number>();
const protectedNodes = new Set<number>();
let protection = 0;
let lastFrameTime = performance.now();
let impactBudget = 0;
const PROTECTION_BUILD = 5.5;
const PROTECTION_RELEASE = 1.35;
const pointer = new THREE.Vector2(8, 8),
  worldPointer = new THREE.Vector3(),
  localPointer = new THREE.Vector3(),
  matrix = new THREE.Matrix4(),
  targetV = new THREE.Vector3(),
  projV = new THREE.Vector3();
let viewW = 1,
  viewH = 1,
  screenX = -100,
  screenY = -100;
const onCreate = () => emit("create");

const BG = "#04060a",
  NODE = "#eef2f6",
  NODE_DIM = "#666d76",
  NODE_ADJ = "#c8cfd6",
  LINK_AGREE = 0.5,
  LINK_DIFFER = 0.22,
  LINK_INFLUENCE = 0.32;
const SPARK_COLORS = [
  [1, 0.48, 0.76],
  [0.74, 0.5, 1],
  [0.36, 0.88, 0.94],
];

const seed = (s: string) => {
  let h = 2166136261;
  for (const c of s) h = Math.imul(h ^ c.charCodeAt(0), 16777619);
  return () => ((h = Math.imul(h ^ (h >>> 15), 2246822507)) >>> 0) / 4294967295;
};
const nodeCount = () =>
  matchMedia("(max-width:720px)").matches
    ? 30 + Math.floor(Math.random() * 10)
    : 52 + Math.floor(Math.random() * 18);
const dustCount = () =>
  matchMedia("(max-width:720px)").matches ? 420 : 1200;
const meteorCount = () =>
  matchMedia("(max-width:720px)").matches ? 180 : 460;
const sparkCount = () =>
  matchMedia("(max-width:720px)").matches ? 260 : 560;

const pos = (i: number, total: number) => {
  if (i < 4) {
    const cube = 0.62;
    return new THREE.Vector3(
      i & 1 ? cube : -cube,
      i & 2 ? cube : -cube,
      i & 1 ? (i & 2 ? cube : -cube) : (i & 2 ? -cube : cube)
    );
  }
  const r = seed(`earth-node-${i}`);
  const golden = Math.PI * (3 - Math.sqrt(5));
  const y = 1 - ((i + 0.5) / total) * 2;
  const radius = 3.08 + (r() - 0.5) * 0.18;
  const ring = Math.sqrt(1 - y * y) * radius;
  const phi = i * golden + r() * 0.18;
  return new THREE.Vector3(
    Math.cos(phi) * ring,
    y * radius,
    Math.sin(phi) * ring
  );
};

const fallbackPoints = computed(() =>
  Array.from({ length: 20 }, (_, i) => {
    const r = seed(`fb-${i}`);
    return {
      id: i,
      style: {
        left: `${8 + r() * 84}%`,
        top: `${8 + r() * 84}%`,
        opacity: `${0.25 + r() * 0.6}`,
        transform: `scale(${0.5 + r() * 0.9})`,
      },
    };
  })
);

const MAX_LINKS = 96;
const buildTree = () => {
  treeLinks = [];
  const forest = [0, Math.floor(base.length * 0.34), Math.floor(base.length * 0.67)];
  const coreLinks = [
    [0, 1], [1, 2], [2, 3], [3, 0], [0, 2],
  ];
  coreLinks.forEach(([s, t], i) => treeLinks.push({ s, t, type: i % 3 === 0 ? 1 : 0 }));
  forest.forEach((root, tree) => {
    const members = [root];
    for (let depth = 0; depth < 3; depth++) {
      const start = members.length;
      for (let k = 0; k < start; k++) {
        const source = members[k];
        const left = (source + 1 + tree * 3) % base.length;
        const right = (source + 2 + tree * 5) % base.length;
        if (!members.includes(left)) members.push(left);
        if (!members.includes(right)) members.push(right);
      }
    }
    members.slice(1).forEach((target, i) => {
      const parent = members[Math.floor((i) / 2)];
      if (target !== parent && treeLinks.length < MAX_LINKS) {
        treeLinks.push({ s: target, t: parent, type: i % 3 === 0 ? 1 : 0 });
      }
    });
  });
};
const buildLinkMesh = () => {
  linkPosition = new Float32Array(MAX_LINKS * 6);
  linkType = new Float32Array(MAX_LINKS * 2);
  linkEmphasis = new Float32Array(MAX_LINKS * 2);
  const g = new THREE.BufferGeometry();
  g.setAttribute("position", new THREE.BufferAttribute(linkPosition, 3));
  g.setAttribute("aType", new THREE.BufferAttribute(linkType, 1));
  g.setAttribute("aEmphasis", new THREE.BufferAttribute(linkEmphasis, 1));
  g.setDrawRange(0, 0);
  linkMat = new THREE.ShaderMaterial({
    transparent: true,
    depthWrite: false,
    uniforms: {
      uAgree: { value: LINK_AGREE },
      uDiffer: { value: LINK_DIFFER },
      uInfluence: { value: LINK_INFLUENCE },
    },
    vertexShader: `
      attribute float aType;
      attribute float aEmphasis;
      varying float vType;
      varying float vEmphasis;
      void main() {
        vType = aType;
        vEmphasis = aEmphasis;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,
    fragmentShader: `
      uniform float uAgree;
      uniform float uDiffer;
      uniform float uInfluence;
      varying float vType;
      varying float vEmphasis;
      void main() {
        float base = vType < 0.5 ? uAgree : (vType < 1.5 ? uDiffer : uInfluence);
        float alpha = clamp(base * vEmphasis, 0.0, 1.0);
        gl_FragColor = vec4(1.0, 1.0, 1.0, alpha);
      }
    `,
  });
  linkMesh = new THREE.LineSegments(g, linkMat);
  group?.add(linkMesh);
};

const build = () => {
  if (!scene) return;
  group?.clear();
  if (!group) {
    group = new THREE.Group();
    scene.add(group);
  }
  group.position.x = matchMedia("(max-width:720px)").matches ? 0.35 : 0.8;
  const count = nodeCount();
  base = Array.from({ length: count }, (_, i) => pos(i, count));
  current = base.map((p) => p.clone());
  galaxyBase = base.map((p, i) => {
    const r = seed(`galaxy-${i}`);
    const angle = (i / Math.max(1, count)) * Math.PI * 2 + r() * 0.55;
    const radius = 1.1 + Math.pow(r(), 0.72) * 38;
    return new THREE.Vector3(
      Math.cos(angle) * radius,
      (r() - 0.5) * (1.4 + radius * 0.34),
      Math.sin(angle) * radius * 0.48 + (r() - 0.5) * 1.2
    ).addScaledVector(p, 0.28);
  });
  nodeScreen = base.map(() => ({ x: 0, y: 0 }));
  nodeDelay = base.map((p) => p.length() * 0.16);
  nodeWeight = base.map((_, i) => (i % 9 === 0 ? 1.5 : 1));
  nodeImpact = base.map(() => 0);
  impactColors = base.map(() => SPARK_COLORS[Math.floor(Math.random() * SPARK_COLORS.length)]);
  const geo = new THREE.IcosahedronGeometry(0.12, 0),
    mat = new THREE.MeshBasicMaterial({
      color: 0xffffff,
      transparent: true,
      opacity: 0.95,
    });
  nodeMesh = new THREE.InstancedMesh(geo, mat, count);
  nodeMesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
  for (let i = 0; i < count; i++) {
    matrix.makeScale(0.001, 0.001, 0.001);
    matrix.setPosition(0, 0, 0);
    nodeMesh.setMatrixAt(i, matrix);
    nodeMesh.setColorAt(i, new THREE.Color(NODE));
  }
  nodeMesh.instanceMatrix.needsUpdate = true;
  group.add(nodeMesh);

  buildTree();
  buildLinkMesh();
  buildDynamicMesh();

  const dn = dustCount();
  dustBase = Array.from(
    { length: dn },
    () =>
      new THREE.Vector3(
        (Math.random() - 0.5) * 30,
        (Math.random() - 0.5) * 13,
        (Math.random() - 0.5) * 6
      )
  );
  dustPositions = new Float32Array(dn * 3);
  dustCur = dustBase.map((p) => p.clone());
  dustVel = dustBase.map(
    () =>
      new THREE.Vector3(
        0.2 + Math.random() * 0.5,
        (Math.random() - 0.5) * 0.16,
        0
      )
  );
  dustCur.forEach((p, i) => {
    dustPositions[i * 3] = p.x;
    dustPositions[i * 3 + 1] = p.y;
    dustPositions[i * 3 + 2] = p.z;
  });
  const dgeo = new THREE.BufferGeometry();
  dgeo.setAttribute("position", new THREE.BufferAttribute(dustPositions, 3));
  const dustMat = new THREE.PointsMaterial({
    size: 0.052,
    transparent: true,
    opacity: 0.42,
    color: new THREE.Color("#8a929b"),
    blending: THREE.AdditiveBlending,
    depthWrite: false,
    sizeAttenuation: true,
  });
  dust = new THREE.Points(dgeo, dustMat);
  scene.add(dust);
  dustPrev = dustCur.map((p) => p.clone());
  dustTrailPositions = new Float32Array(dn * 6);
  const tgeo = new THREE.BufferGeometry();
  tgeo.setAttribute("position", new THREE.BufferAttribute(dustTrailPositions, 3));
  dustTrailMat = new THREE.LineBasicMaterial({
    transparent: true,
    opacity: 0,
    color: new THREE.Color("#cdd6e0"),
    blending: THREE.AdditiveBlending,
    depthWrite: false,
  });
  dustTrails = new THREE.LineSegments(tgeo, dustTrailMat);
  dustTrails.frustumCulled = false;
  scene.add(dustTrails);

  const mn = meteorCount();
  meteorCur = Array.from(
    { length: mn },
    () => {
      const crossing = Math.random() < 0.9;
      return new THREE.Vector3(
        crossing ? -18 + Math.random() * 6 : -16 + Math.random() * 20,
        crossing
          ? (Math.random() - 0.5) * 5.4
          : (Math.random() - 0.5) * 5.2,
        (Math.random() - 0.5) * 6
      );
    }
  );
  meteorVel = meteorCur.map(() => {
    const reverse = Math.random() < 0.12;
    const base = reverse ? Math.PI : -0.015;
    const speed =
      Math.random() < 0.3 ? 1.8 + Math.random() * 3.2 : 0.5 + Math.random() * 1;
    const ang = base + (Math.random() - 0.5) * 0.18;
    return new THREE.Vector3(
      Math.cos(ang) * speed,
      Math.sin(ang) * speed,
      (Math.random() - 0.5) * 0.08
    );
  });
  meteorOmega = new Float32Array(mn);
  for (let i = 0; i < mn; i++) meteorOmega[i] = (Math.random() - 0.5) * 0.14;
  meteorPositions = new Float32Array(mn * 18);
  meteorCur.forEach((p, i) => {
    for (let k = 0; k < 6; k++) {
      meteorPositions[i * 18 + k * 3] = p.x;
      meteorPositions[i * 18 + k * 3 + 1] = p.y;
      meteorPositions[i * 18 + k * 3 + 2] = p.z;
    }
  });
  const mgeo = new THREE.BufferGeometry();
  mgeo.setAttribute("position", new THREE.BufferAttribute(meteorPositions, 3));
  meteors = new THREE.LineSegments(
    mgeo,
    new THREE.LineBasicMaterial({
      transparent: true,
      opacity: 0.7,
      color: new THREE.Color("#eef2f6"),
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    })
  );
  scene.add(meteors);
  nodeWorldV = base.map(() => new THREE.Vector3());

  const sc = sparkCount();
  sparkPositions = new Float32Array(sc * 3);
  sparkColors = new Float32Array(sc * 3);
  sparkLife = new Float32Array(sc);
  sparkVel = new Float32Array(sc * 3);
  sparkBase = new Float32Array(sc * 3);
  sparkCursor = 0;
  for (let i = 0; i < sc; i++) sparkPositions[i * 3 + 1] = -100;
  const sgeo = new THREE.BufferGeometry();
  sgeo.setAttribute("position", new THREE.BufferAttribute(sparkPositions, 3));
  sgeo.setAttribute("color", new THREE.BufferAttribute(sparkColors, 3));
  sparks = new THREE.Points(
    sgeo,
    new THREE.PointsMaterial({
      size: 0.052,
      transparent: true,
      vertexColors: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      sizeAttenuation: true,
    })
  );
  scene.add(sparks);

  burstMesh = new THREE.Mesh(
    new THREE.IcosahedronGeometry(1, 0),
    new THREE.MeshBasicMaterial({ color: "#111820", transparent: true, opacity: 0 })
  );
  burstMesh.visible = false;
  scene.add(burstMesh);

  grid = new THREE.GridHelper(
    60,
    60,
    new THREE.Color("#ffffff"),
    new THREE.Color("#ffffff")
  );
  grid.position.y = -3;
  (grid.material as THREE.Material).transparent = true;
  (grid.material as THREE.Material).opacity = 0.4;
  // Keep the coordinate plane stable while the opinion network rotates above it.
  scene.add(grid);
};

const buildDynamicMesh = () => {
  dynamicSlots = matchMedia("(max-width:720px)").matches
    ? DYNAMIC_NODE_LIMIT_MOBILE
    : DYNAMIC_NODE_LIMIT_DESKTOP;
  dynamicNodes = Array.from({ length: dynamicSlots });
  nextDynamicSpawnAt = performance.now() * 0.001 + 7 + Math.random() * 7;
  const geo = new THREE.CylinderGeometry(0.14, 0.14, 0.06, 6);
  const mat = new THREE.MeshBasicMaterial({
    color: 0xffffff,
    transparent: true,
    opacity: 0.94,
  });
  dynamicMesh = new THREE.InstancedMesh(geo, mat, dynamicSlots);
  dynamicMesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
  for (let i = 0; i < dynamicSlots; i++) {
    matrix.makeScale(0.001, 0.001, 0.001);
    matrix.setPosition(0, 0, 0);
    dynamicMesh.setMatrixAt(i, matrix);
    dynamicMesh.setColorAt(i, new THREE.Color("#ffffff"));
  }
  dynamicMesh.instanceMatrix.needsUpdate = true;
  group?.add(dynamicMesh);

  const positions = new Float32Array(dynamicSlots * 6);
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
  geometry.setDrawRange(0, 0);
  dynamicLinkMesh = new THREE.LineSegments(
    geometry,
    new THREE.LineBasicMaterial({
      color: new THREE.Color("#d8e4ee"),
      transparent: true,
      opacity: 0.58,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    })
  );
  group?.add(dynamicLinkMesh);
};

const dynamicTarget = () => {
  const index = Math.floor(Math.random() * base.length);
  return { index, point: base[index].clone().normalize().multiplyScalar(3.18) };
};

const spawnDynamicNode = (now: number) => {
  const slot = dynamicNodes.findIndex((node) => !node);
  if (slot < 0 || !base.length) return;
  const { index, point } = dynamicTarget();
  const side = Math.random() < 0.5 ? -1 : 1;
  const start = new THREE.Vector3(
    side * (9.2 + Math.random() * 2),
    (Math.random() - 0.5) * 5.4,
    (Math.random() - 0.5) * 2.5
  );
  const willAnchor = shouldAnchorDynamicNode();
  dynamicNodes[slot] = {
    slot,
    state: "arriving",
    position: start.clone(),
    start,
    target: point,
    departureTarget: point.clone(),
    linkedCore: index,
    age: 0,
    ttl: 4 + Math.random() * 5,
    departureAt: Infinity,
    willAnchor,
    alpha: 0,
  };
  nextDynamicSpawnAt = scheduleNextSpawn(now, Math.random);
};

const beginDynamicDeparture = (node: DynamicNode, now: number) => {
  node.state = "departing";
  node.age = 0;
  node.start.copy(node.position);
  const outward = node.position.clone().normalize();
  if (outward.lengthSq() < 0.01) outward.set(Math.random() < 0.5 ? -1 : 1, 0, 0);
  node.departureTarget = node.position
    .clone()
    .add(outward.multiplyScalar(8 + Math.random() * 3))
    .add(new THREE.Vector3((Math.random() - 0.5) * 3, (Math.random() - 0.5) * 2, 0));
  node.departureAt = now;
};

const updateDynamicNodes = (now: number, dt: number) => {
  if (!dynamicMesh || !dynamicLinkMesh) return;
  const progress = props.progress;
  const reduced = matchMedia("(prefers-reduced-motion:reduce)").matches;
  if (shouldPauseDynamicMotion(progress, reduced)) return;
  const active = dynamicNodes.filter(Boolean) as DynamicNode[];
  const due = active
    .filter((node) => node.state !== "departing" && node.departureAt <= now)
    .sort((a, b) => a.departureAt - b.departureAt)[0];
  if (due) beginDynamicDeparture(due, now);
  else if (active.length < dynamicSlots && now >= nextDynamicSpawnAt) spawnDynamicNode(now);

  for (let i = 0; i < dynamicSlots; i++) {
    const node = dynamicNodes[i];
    if (!node) {
      matrix.makeScale(0.001, 0.001, 0.001);
      matrix.setPosition(0, 0, 0);
      dynamicMesh.setMatrixAt(i, matrix);
      continue;
    }
    node.age += dt;
    if (node.state === "arriving") {
      const p = Math.min(1, node.age / 2.8);
      const eased = 1 - Math.pow(1 - p, 3);
      node.position.lerpVectors(node.start, node.target, eased);
      node.alpha = Math.min(1, p * 1.5);
      if (p >= 1) {
        if (node.willAnchor) {
          node.state = "anchored";
          node.departureAt = now + 12 + Math.random() * 14;
        } else {
          node.state = "transient";
          node.linkedCore = -1;
          node.departureAt = now + node.ttl;
        }
      }
    } else if (node.state === "departing") {
      const p = Math.min(1, node.age / 2.4);
      node.position.lerpVectors(node.start, node.departureTarget, 1 - Math.pow(1 - p, 3));
      node.alpha = 1 - p;
      if (p >= 1) {
        dynamicNodes[i] = undefined;
        nextDynamicSpawnAt = Math.max(nextDynamicSpawnAt, now + 2 + Math.random() * 3);
        continue;
      }
    } else {
      node.alpha = 1;
    }
    const scale = 0.72 * Math.max(0.001, node.alpha);
    matrix.compose(node.position, dynamicQuaternion, new THREE.Vector3(scale, scale, scale));
    dynamicMesh.setMatrixAt(i, matrix);
  }
  dynamicMesh.instanceMatrix.needsUpdate = true;
  if (dynamicMesh.instanceColor) dynamicMesh.instanceColor.needsUpdate = true;

  const positions = dynamicLinkMesh.geometry.attributes.position.array as Float32Array;
  let lineCount = 0;
  dynamicNodes.forEach((node) => {
    if (!node || node.linkedCore < 0 || node.alpha <= 0.02) return;
    const core = current[node.linkedCore];
    if (!core) return;
    const offset = lineCount * 6;
    positions[offset] = node.position.x;
    positions[offset + 1] = node.position.y;
    positions[offset + 2] = node.position.z;
    positions[offset + 3] = core.x;
    positions[offset + 4] = core.y;
    positions[offset + 5] = core.z;
    lineCount++;
  });
  dynamicLinkMesh.geometry.setDrawRange(0, lineCount * 2);
  dynamicLinkMesh.geometry.attributes.position.needsUpdate = true;
};

const findFocus = () => {
  if (pointer.x >= 2 || introT < 2.2) {
    focusIndex = -1;
    adjacent.clear();
    return;
  }
  let best = -1,
    bestD = Infinity;
  for (let i = 0; i < current.length; i++) {
    projV.copy(current[i]);
    group?.localToWorld(projV);
    projV.project(camera!);
    const sx = (projV.x * 0.5 + 0.5) * viewW,
      sy = (-projV.y * 0.5 + 0.5) * viewH;
    nodeScreen[i].x = sx;
    nodeScreen[i].y = sy;
    const d = Math.hypot(sx - screenX, sy - screenY);
    if (d < bestD) {
      bestD = d;
      best = i;
    }
  }
  if (best >= 0 && bestD < 220) focusIndex = best;
  else focusIndex = -1;
  adjacent.clear();
  if (focusIndex >= 0) {
    const fx = nodeScreen[focusIndex].x,
      fy = nodeScreen[focusIndex].y;
    for (let i = 0; i < nodeScreen.length; i++) {
      if (i === focusIndex) continue;
      const d = Math.hypot(nodeScreen[i].x - fx, nodeScreen[i].y - fy);
      if (d < 130) adjacent.add(i);
    }
  }
};

const easeOut = (t: number) => 1 - Math.pow(1 - Math.max(0, Math.min(1, t)), 3);
const easeInOut = (t: number) => {
  const p = Math.max(0, Math.min(1, t));
  return p * p * (3 - 2 * p);
};
const globalProgress = (progress: number) => {
  const t = Math.max(0, Math.min(1, (progress - 0.55) / 0.45));
  return t * t * (3 - 2 * t);
};

const animate = () => {
  if (!nodeMesh || !linkMesh) return;
  const reduced = matchMedia("(prefers-reduced-motion:reduce)").matches;
  const now = performance.now();
  const dt = Math.min(0.05, Math.max(0.001, (now - lastFrameTime) / 1000));
  lastFrameTime = now;
  const t = performance.now() * 0.001;
  if (props.selection !== selectionTarget) {
    selectionTarget = props.selection;
    selectionFrom = selectionMix;
    selectionStartedAt = now;
  }
  const selectionElapsed = Math.min(1, (now - selectionStartedAt) / selectionDuration);
  const selectionEnd = selectionTarget ? 1 : 0;
  selectionMix = selectionFrom + (selectionEnd - selectionFrom) * easeInOut(selectionElapsed);
  if (props.burst !== burstSeen && current.length && camera && burstMesh) {
    burstSeen = props.burst;
    burstStartedAt = now;
    burstIndex = Math.floor(Math.random() * current.length);
    burstStart.copy(current[burstIndex]);
    group?.localToWorld(burstStart);
    camera.getWorldDirection(burstForward);
    burstEnd.copy(camera.position).addScaledVector(burstForward, .24);
    burstMesh.position.copy(burstStart);
    burstMesh.scale.setScalar(.12);
    burstMesh.visible = true;
  }
  if (burstMesh?.visible && burstStartedAt >= 0) {
    const raw = Math.min(1, (now - burstStartedAt) / NODE_BURST_DURATION);
    const move = 1 - Math.pow(1 - raw, 4);
    burstMesh.position.lerpVectors(burstStart, burstEnd, move);
    burstMesh.scale.setScalar(.12 + move * 8.8);
    const material = burstMesh.material as THREE.MeshBasicMaterial;
    material.opacity = raw < .82 ? Math.min(1, raw * 2.4) : 1 - (raw - .82) / .18;
    if (raw >= 1) burstMesh.visible = false;
  }
  const global = globalProgress(props.progress);
  if (introT < 2.2) introT += 0.016;
  if (!reduced) findFocus();
  updateProtection(dt);
  impactBudget = 2;
  const focus = focusIndex;
  if (focus !== lastFocus) {
    lastFocus = focus;
    emit("connect", focus >= 0 ? adjacent.size : 0);
  }
  for (let i = 0; i < base.length; i++) {
    const home = base[i];
    let tx = home.x,
      ty = home.y,
      tz = home.z;
    if (i === focus && !reduced) {
      tx += (localPointer.x - home.x) * 0.4;
      ty += (localPointer.y - home.y) * 0.4;
      tz += (localPointer.z - home.z) * 0.4;
    }
    const local = reduced
      ? 1
      : easeOut((introT - nodeDelay[i]) / 1.5);
    if (nodeImpact[i] > 0) {
      tx += Math.sin(t * 9 + i) * nodeImpact[i] * 0.28;
      ty += Math.cos(t * 8 + i * 0.7) * nodeImpact[i] * 0.22;
    }
    if (global > 0) {
      tx += (galaxyBase[i].x - tx) * global;
      ty += (galaxyBase[i].y - ty) * global;
      tz += (galaxyBase[i].z - tz) * global;
    }
    targetV.set(tx, ty, tz).multiplyScalar(local);
    current[i].lerp(targetV, reduced ? 1 : 0.1);
    nodeImpact[i] = Math.max(0, nodeImpact[i] - (reduced ? 1 : 0.055));
    const breath = 1 + Math.sin(t * 1.4 + i * 0.5) * 0.03;
    const isFocus = i === focus,
      isAdj = adjacent.has(i),
      isProtected = protectedNodes.has(i);
    const coreWeight = Math.max(0, 1 - galaxyBase[i].length() / 4.8);
    const globalPulse = global * (0.5 + 0.5 * Math.sin(t * 0.9));
    const s =
      nodeWeight[i] *
      (isFocus ? 1.35 : isAdj ? 1.06 : 1) *
      breath *
      (1 + nodeImpact[i] * 0.28) *
      (isProtected ? 1 + protection * 0.08 : 1) *
      (1 + coreWeight * globalPulse * 0.2) *
      (local < 0.02 ? 0.001 : local);
    matrix.makeScale(s, s, s);
    matrix.setPosition(current[i]);
    nodeMesh.setMatrixAt(i, matrix);
    const baseColor = new THREE.Color(isFocus || isProtected ? "#ffffff" : isAdj ? NODE_ADJ : NODE_DIM);
    const col = nodeImpact[i] > 0.02
      ? new THREE.Color(...impactColors[i]).lerp(baseColor, 1 - Math.min(1, nodeImpact[i]))
      : baseColor;
    nodeMesh.setColorAt(i, col);
  }
  updateDynamicNodes(t, dt);
  nodeMesh.instanceMatrix.needsUpdate = true;
  if (nodeMesh.instanceColor) nodeMesh.instanceColor.needsUpdate = true;

  activeLinks = [];
  if (focus >= 0) {
    adjacent.forEach((j) => {
      const r = seed(`dl-${focus}-${j}`)();
      activeLinks.push({
        s: focus,
        t: j,
        type: r < 0.55 ? 0 : r < 0.85 ? 1 : 2,
      });
    });
  }
  linkFade += ((activeLinks.length ? 1 : 0) - linkFade) * 0.12;
  const writeLink = (l: { s: number; t: number; type: number }, i: number, em: number) => {
    if (i >= MAX_LINKS) return;
    linkPosition[i * 6] = current[l.s].x;
    linkPosition[i * 6 + 1] = current[l.s].y;
    linkPosition[i * 6 + 2] = current[l.s].z;
    linkPosition[i * 6 + 3] = current[l.t].x;
    linkPosition[i * 6 + 4] = current[l.t].y;
    linkPosition[i * 6 + 5] = current[l.t].z;
    linkType[i * 2] = l.type;
    linkType[i * 2 + 1] = l.type;
    linkEmphasis[i * 2] = em;
    linkEmphasis[i * 2 + 1] = em;
  };
  treeLinks.forEach((l, i) => {
    // 核心四节点的几何连线保持常态可见，撞击时只叠加亮度变化。
    const coreEmphasis = i < 5 ? 0.9 : 0.62;
    writeLink(l, i, coreEmphasis + global * 0.2);
  });
  activeLinks.forEach((l, i) =>
    writeLink(l, i + treeLinks.length, linkFade * (0.72 + protection * 0.28))
  );
  const total = Math.min(treeLinks.length + activeLinks.length, MAX_LINKS);
  linkMesh.geometry.setDrawRange(0, total * 2);
  (linkMesh.geometry.attributes.position as THREE.BufferAttribute).needsUpdate = true;
  (linkMesh.geometry.attributes.aType as THREE.BufferAttribute).needsUpdate = true;
  (linkMesh.geometry.attributes.aEmphasis as THREE.BufferAttribute).needsUpdate = true;
  (linkMesh.geometry.attributes.aEmphasis as THREE.BufferAttribute).needsUpdate = true;

  if (dust && dustPositions.length) {
    const pp = props.progress;
    if (dustTrailMat) dustTrailMat.opacity = 0.16 + pp * 0.58;
    for (let i = 0; i < dustCur.length; i++) {
      dustPrev[i].copy(dustCur[i]);
      const p = dustCur[i];
      if (pp > 0.15) {
        const ang = 0.0009 * pp;
        const cos = Math.cos(ang),
          sin = Math.sin(ang),
          x = p.x,
          z = p.z;
        p.x = x * cos + z * sin;
        p.z = -x * sin + z * cos;
      }
      const slow = 1 - pp * 0.6 - global * 0.22;
      if (p.x < 5.5) {
        const cx = group?.position.x || 0,
          cy = group?.position.y || 0,
          gx = cx - p.x,
          gy = cy - p.y,
          gd = Math.sqrt(gx * gx + gy * gy) || 1;
        const gravity = Math.min(0.035, 0.012 + (5.5 - p.x) * 0.002);
        dustVel[i].x += (gx / gd) * gravity - (gy / gd) * 0.012;
        dustVel[i].y += (gy / gd) * gravity + (gx / gd) * 0.012;
      }
      p.x += dustVel[i].x * slow;
      p.y += dustVel[i].y * slow;
      p.z += dustVel[i].z * slow;
      if (p.x > 20) p.x = -20;
      if (p.y > 6) p.y = -6;
      if (p.y < -6) p.y = 6;
      if (p.z > 3) p.z = -3;
      if (p.z < -3) p.z = 3;
      dustPositions[i * 3] = p.x;
      dustPositions[i * 3 + 1] = p.y;
      dustPositions[i * 3 + 2] = p.z;
      const o = i * 6;
      if (i % 3 === 0 && pp > 0.18) {
        const dx = p.x - dustPrev[i].x,
          dy = p.y - dustPrev[i].y,
          dz = p.z - dustPrev[i].z,
          d = Math.sqrt(dx * dx + dy * dy + dz * dz) || 1e-4,
          len = Math.min(1.45, d * (1.8 + pp * 4.5));
        dustTrailPositions[o] = p.x - (dx / d) * len;
        dustTrailPositions[o + 1] = p.y - (dy / d) * len;
        dustTrailPositions[o + 2] = p.z - (dz / d) * len;
        dustTrailPositions[o + 3] = p.x;
        dustTrailPositions[o + 4] = p.y;
        dustTrailPositions[o + 5] = p.z;
      } else {
        dustTrailPositions[o] = p.x;
        dustTrailPositions[o + 1] = p.y;
        dustTrailPositions[o + 2] = p.z;
        dustTrailPositions[o + 3] = p.x;
        dustTrailPositions[o + 4] = p.y;
        dustTrailPositions[o + 5] = p.z;
      }
    }
    dust.geometry.attributes.position.needsUpdate = true;
    if (dustTrails) dustTrails.geometry.attributes.position.needsUpdate = true;
  }
  if (meteors && meteorPositions.length) {
    const pp = props.progress;
    const meteorMaterial = meteors.material as THREE.LineBasicMaterial;
    meteorMaterial.opacity = 0.7 + global * 0.16;
    for (let j = 0; j < base.length; j++) {
      nodeWorldV[j].copy(current[j]);
      group?.localToWorld(nodeWorldV[j]);
    }
    for (let i = 0; i < meteorCur.length; i++) {
      const p = meteorCur[i];
      const v = meteorVel[i];
      const om = meteorOmega[i];
      const c = Math.cos(om),
        s = Math.sin(om);
      const nvx = v.x * c - v.y * s,
        nvy = v.x * s + v.y * c;
      v.x = nvx;
      v.y = nvy;
      let defX = 0,
        defY = 0,
        maxF = 0,
        protectedF = 0,
        protectedNode = -1;
      for (let j = 0; j < nodeWorldV.length; j++) {
        const nw = nodeWorldV[j];
        const dx = p.x - nw.x,
          dy = p.y - nw.y;
        const d2 = dx * dx + dy * dy;
        if (d2 < 1.35) {
          const d = Math.sqrt(d2) || 1e-4;
          const f = 1 - d / Math.sqrt(1.35);
          if (f > maxF) maxF = f;
          defX += (dx / d) * f * 0.42;
          defY += (dy / d) * f * 0.42;
          v.x *= 1 - f * 0.16;
          v.y *= 1 - f * 0.16;
          nodeImpact[j] = Math.max(nodeImpact[j], f * 1.05);
          // Make every close strike physically legible: the node recoils and
          // eases back through the existing target interpolation.
          if (f > 0.18) {
            current[j].x += (dx / d) * f * 0.12;
            current[j].y += (dy / d) * f * 0.12;
          }
          if (protectedNodes.has(j) && f > protectedF) {
            protectedF = f;
            protectedNode = j;
          }
        }
      }
      if (protectedF > 0.04 && protectedNode >= 0) {
        const node = nodeWorldV[protectedNode];
        const shieldDx = p.x - node.x;
        const shieldDy = p.y - node.y;
        const shieldDistance = Math.sqrt(shieldDx * shieldDx + shieldDy * shieldDy) || 1e-4;
        const resistance = protection * protectedF;
        defX += (shieldDx / shieldDistance) * resistance * 1.15;
        defY += (shieldDy / shieldDistance) * resistance * 1.15;
        v.x *= 1 - resistance * 0.34;
        v.y *= 1 - resistance * 0.34;
        if (impactBudget > 0 && protectedF > 0.18) {
          impactBudget--;
          const idx = sparkCursor % sparkLife.length;
          sparkCursor++;
          const color = SPARK_COLORS[(protectedNode + i) % SPARK_COLORS.length];
          sparkPositions[idx * 3] = p.x;
          sparkPositions[idx * 3 + 1] = p.y;
          sparkPositions[idx * 3 + 2] = p.z;
          sparkVel[idx * 3] = (shieldDx / shieldDistance) * 0.7;
          sparkVel[idx * 3 + 1] = (shieldDy / shieldDistance) * 0.7;
          sparkVel[idx * 3 + 2] = 0;
          sparkBase[idx * 3] = color[0];
          sparkBase[idx * 3 + 1] = color[1];
          sparkBase[idx * 3 + 2] = color[2];
          sparkLife[idx] = 0.12 + protection * 0.06;
        }
      } else if (maxF > 0.15) {
        const count = Math.min(2, Math.round(maxF * 4));
        for (let k = 0; k < count; k++) {
          const idx = sparkCursor % sparkLife.length;
          sparkCursor++;
          const c = SPARK_COLORS[Math.floor(Math.random() * SPARK_COLORS.length)];
          sparkPositions[idx * 3] = p.x;
          sparkPositions[idx * 3 + 1] = p.y;
          sparkPositions[idx * 3 + 2] = p.z;
          sparkVel[idx * 3] = (Math.random() - 0.5) * 0.45;
          sparkVel[idx * 3 + 1] = (Math.random() - 0.5) * 0.45 + 0.15;
          sparkVel[idx * 3 + 2] = (Math.random() - 0.5) * 0.45;
          sparkBase[idx * 3] = c[0];
          sparkBase[idx * 3 + 1] = c[1];
          sparkBase[idx * 3 + 2] = c[2];
          sparkLife[idx] = 0.24;
        }
      }
      const ms = 1 - pp * 0.4 - global * 0.24;
      p.x += (v.x + defX) * ms;
      p.y += (v.y + defY) * ms;
      p.z += v.z * ms;
      if (p.x > 20) p.x = -20;
      if (p.y > 6.5) p.y = -6.5;
      if (p.y < -6.5) p.y = 6.5;
      if (p.z > 3) p.z = -3;
      if (p.z < -3) p.z = 3;
      const speed = meteorVel[i].length();
      const inv = 1 / (speed || 1);
      const dirx = v.x * inv,
        diry = v.y * inv,
        dirz = v.z * inv;
      const step = Math.min(0.5, speed * (0.06 + pp * 0.05));
      const rot = (dx: number, dy: number, a: number) => {
        const cc = Math.cos(a),
          ss = Math.sin(a);
        return [dx * cc - dy * ss, dx * ss + dy * cc] as const;
      };
      const [d1x, d1y] = rot(dirx, diry, -om * 0.5);
      const [d2x, d2y] = rot(dirx, diry, -om * 1);
      const [d3x, d3y] = rot(dirx, diry, -om * 1.5);
      const p1x = p.x - d1x * step,
        p1y = p.y - d1y * step,
        p1z = p.z - dirz * step;
      const p2x = p1x - d2x * step,
        p2y = p1y - d2y * step,
        p2z = p1z - dirz * step;
      const p3x = p2x - d3x * step,
        p3y = p2y - d3y * step,
        p3z = p2z - dirz * step;
      const o = i * 18;
      meteorPositions[o] = p.x;
      meteorPositions[o + 1] = p.y;
      meteorPositions[o + 2] = p.z;
      meteorPositions[o + 3] = p1x;
      meteorPositions[o + 4] = p1y;
      meteorPositions[o + 5] = p1z;
      meteorPositions[o + 6] = p1x;
      meteorPositions[o + 7] = p1y;
      meteorPositions[o + 8] = p1z;
      meteorPositions[o + 9] = p2x;
      meteorPositions[o + 10] = p2y;
      meteorPositions[o + 11] = p2z;
      meteorPositions[o + 12] = p2x;
      meteorPositions[o + 13] = p2y;
      meteorPositions[o + 14] = p2z;
      meteorPositions[o + 15] = p3x;
      meteorPositions[o + 16] = p3y;
      meteorPositions[o + 17] = p3z;
    }
    meteors.geometry.attributes.position.needsUpdate = true;
  }
  if (sparks && sparkPositions.length) {
    for (let i = 0; i < sparkLife.length; i++) {
      if (sparkLife[i] > 0) {
        sparkLife[i] -= 0.012;
        sparkPositions[i * 3] += sparkVel[i * 3];
        sparkPositions[i * 3 + 1] += sparkVel[i * 3 + 1];
        sparkPositions[i * 3 + 2] += sparkVel[i * 3 + 2];
        sparkVel[i * 3] *= 0.97;
        sparkVel[i * 3 + 1] *= 0.97;
        sparkVel[i * 3 + 2] *= 0.97;
        const l = Math.max(0, sparkLife[i]);
        sparkColors[i * 3] = sparkBase[i * 3] * l;
        sparkColors[i * 3 + 1] = sparkBase[i * 3 + 1] * l;
        sparkColors[i * 3 + 2] = sparkBase[i * 3 + 2] * l;
      } else {
        sparkColors[i * 3] = 0;
        sparkColors[i * 3 + 1] = 0;
        sparkColors[i * 3 + 2] = 0;
      }
    }
    sparks.geometry.attributes.position.needsUpdate = true;
    sparks.geometry.attributes.color.needsUpdate = true;
  }
};

const updatePointer = (e: PointerEvent) => {
  if (!host.value || !camera) return;
  screenX = e.clientX;
  screenY = e.clientY;
  const r = host.value.getBoundingClientRect();
  pointer.set(
    ((e.clientX - r.left) / r.width) * 2 - 1,
    -(((e.clientY - r.top) / r.height) * 2 - 1)
  );
  const plane = new THREE.Plane(new THREE.Vector3(0, 0, 1), 0);
  const ray = new THREE.Raycaster();
  ray.setFromCamera(pointer, camera);
  ray.ray.intersectPlane(plane, worldPointer);
  localPointer.copy(worldPointer);
  group?.worldToLocal(localPointer);
};
const leave = () => {
  pointer.set(8, 8);
};
const updateOrientation = (event: DeviceOrientationEvent) => {
  if (!matchMedia("(max-width: 720px)").matches) return;
  const gamma = Math.max(-25, Math.min(25, event.gamma || 0));
  const beta = Math.max(-25, Math.min(25, (event.beta || 45) - 45));
  pointer.set(gamma / 25, -beta / 25);
};
const updateProtection = (dt: number) => {
  const target = focusIndex >= 0 ? 1 : 0;
  const rate = target > protection ? PROTECTION_BUILD : PROTECTION_RELEASE;
  protection += (target - protection) * Math.min(1, dt * rate);
  protectedNodes.clear();
  if (focusIndex < 0 || protection < 0.01) return;
  protectedNodes.add(focusIndex);
  adjacent.forEach((index) => protectedNodes.add(index));
};
const resize = () => {
  if (!host.value || !renderer || !camera) return;
  const rect = host.value.getBoundingClientRect();
  const w = Math.max(rect.width, 1),
    h = Math.max(rect.height, 1);
  viewW = w;
  viewH = h;
  renderer.setSize(w, h);
  camera.aspect = w / h;
  camera.updateProjectionMatrix();
};
const draw = () => {
  if (renderer && scene && camera && visible && !props.paused) {
    if (props.bursting !== transparentClear) {
      transparentClear = props.bursting;
      scene.background = transparentClear ? null : new THREE.Color(BG);
      renderer.setClearColor(new THREE.Color(BG), transparentClear ? 0 : 1);
    }
    animate();
    if (group && !matchMedia("(prefers-reduced-motion:reduce)").matches) {
      const global = globalProgress(props.progress);
      const globalImpact = global * global;
      const px = pointer.x < 2 ? pointer.x : 0,
        py = pointer.y < 2 ? pointer.y : 0,
        p = props.progress;
      const resistance = 1 - protection * 0.72;
      group.rotation.y += 0.00055 * resistance * (1 - p * 0.55);
      const targetRX = p * -1.5 + py * 0.16 * (1 - p);
      group.rotation.x += (targetRX - group.rotation.x) * 0.06;
      if (grid) {
        // The grid is its own spatial reference: keep it level at the hero,
        // then turn it directly toward the viewer as the plaza is revealed.
        const gridTilt = p * -1.5;
        grid.rotation.x += (gridTilt - grid.rotation.x) * 0.06;
        grid.position.y = -3 + p * 1.5 + selectionMix * .72;
      }
      group.position.y += (p * 1.5 + globalImpact * 0.8 - group.position.y) * 0.06;
      const gx = matchMedia("(max-width:720px)").matches ? 1.2 : 2.4;
      group.position.x += ((1 - p) * gx - globalImpact * 1.25 - group.position.x) * 0.06;
      const homeZ = 12.5 + p * 1.4 + globalImpact * 45;
      const homeY = py * .2;
      camera.position.z = homeZ + (3.4 - homeZ) * selectionMix;
      camera.position.y = homeY + (3.25 - homeY) * selectionMix;
      camera.position.x = px * .3 * (1 - selectionMix);
      selectionLookAt.set(
        (group?.position.x || 0) * selectionMix,
        (group?.position.y || 0) - selectionMix * .35,
        0
      );
      camera.lookAt(selectionLookAt);
      const homeFov = 46 + globalImpact * 12;
      camera.fov = homeFov + (50 - homeFov) * selectionMix;
      camera.updateProjectionMatrix();
      const starRecede = p * 3.5;
      if (dust) dust.position.z = -starRecede;
      if (meteors) meteors.position.z = -starRecede * 1.1;
    }
    renderer.render(scene, camera);
  }
  frame = requestAnimationFrame(draw);
};

onMounted(() => {
  if (!host.value) return;
  try {
    scene = new THREE.Scene();
    scene.background = new THREE.Color(BG);
    scene.fog = new THREE.Fog(BG, 9, 24);
    camera = new THREE.PerspectiveCamera(46, 1, 0.1, 100);
    camera.position.z = 12.5;
    renderer = new THREE.WebGLRenderer({
      alpha: true,
      antialias: true,
      // Avoid Windows GPU adapter selection failures in browsers that reject
      // the high-performance hint even though WebGL itself is available.
      powerPreference: "default",
      failIfMajorPerformanceCaveat: false,
    });
    renderer.setClearColor(new THREE.Color(BG), 1);
    renderer.setPixelRatio(Math.min(devicePixelRatio, 1.5));
    host.value.appendChild(renderer.domElement);
    build();
    requestAnimationFrame(() => resize());
    observer = new ResizeObserver(resize);
    observer.observe(host.value);
    window.addEventListener("resize", resize);
    window.addEventListener("pointermove", updatePointer);
    window.addEventListener("deviceorientation", updateOrientation, { passive: true });
    window.addEventListener("click", onCreate);
    document.addEventListener("pointerleave", leave);
    document.addEventListener(
      "visibilitychange",
      () => (visible = !document.hidden)
    );
    draw();
  } catch (error) {
    console.error("[HeroField] Three.js initialization failed", error);
    fallback.value = true;
  }
});
onBeforeUnmount(() => {
  cancelAnimationFrame(frame);
  observer?.disconnect();
  window.removeEventListener("resize", resize);
  window.removeEventListener("pointermove", updatePointer);
  window.removeEventListener("deviceorientation", updateOrientation);
  window.removeEventListener("click", onCreate);
  document.removeEventListener("pointerleave", leave);
  renderer?.dispose();
  renderer?.domElement.remove();
});
</script>
<style scoped>
.hero-field {
  position: absolute;
  inset: 0;
  overflow: hidden;
  outline: 0;
  cursor: default;
}
.hero-field canvas {
  width: 100%;
  height: 100%;
  display: block;
}
.fallback span {
  position: absolute;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #fff;
}
</style>
